// DISC Insights — Training section. Shows the caller DISC mix, per-style
// performance (avg grading score, revival win rate) and the coaching
// guidance for selling to each behavioural style. Rule-based, no AI cost.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Fingerprint, Loader2, Phone, Trophy, Target, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE_META = {
  D: {
    name: 'Dominant', tag: 'Direct · results-driven', tone: 'rose',
    headerCls: 'bg-rose-50 border-rose-200', badgeCls: 'bg-rose-600',
    coach: {
      approach: 'Be brief, be bold, lead with the outcome. Give them control and choices — never a lecture.',
      do: ['Lead with the result they get', 'Offer two clear options', 'Match their pace — fast and direct'],
      dont: ['Small talk or long warm-ups', 'Over-explaining features', 'Hedging ("maybe", "we could possibly")'],
      greenBrain: '“What\u2019s the number one result you want in the next 90 days?”',
      objection: 'Acknowledge in one line, then bottom-line the value and close with an alternate-choice: “Today at 4 or tomorrow at 10?”',
    },
  },
  I: {
    name: 'Influential', tag: 'Enthusiastic · people-first', tone: 'amber',
    headerCls: 'bg-amber-50 border-amber-200', badgeCls: 'bg-amber-500',
    coach: {
      approach: 'Match their energy. Sell the experience and the community, not the spec sheet.',
      do: ['Use stories and social proof', 'Talk about the people and vibe', 'Keep it upbeat and personal'],
      dont: ['Drowning them in detail', 'Flat, monotone delivery', 'Letting the chat drift without a next step'],
      greenBrain: '“Who would you love to share this journey with?”',
      objection: 'Stay positive, use a member story that mirrors their worry, then book a fun, low-pressure visit.',
    },
  },
  S: {
    name: 'Steady', tag: 'Warm · cautious · loyal', tone: 'emerald',
    headerCls: 'bg-emerald-50 border-emerald-200', badgeCls: 'bg-emerald-600',
    coach: {
      approach: 'Slow down. Reassure, go step-by-step, and never rush the decision. Trust wins this style.',
      do: ['Listen more than you talk', 'Lay out a simple step-by-step plan', 'Reassure: support, no lock-in surprises'],
      dont: ['Pressure or countdown tactics', 'Skipping their questions', 'Big sudden changes'],
      greenBrain: '“What would feeling healthier let you do with the people you care about?”',
      objection: 'Empathise first, then offer a low-risk next step (a visit or trial) and promise to walk them through everything.',
    },
  },
  C: {
    name: 'Conscientious', tag: 'Analytical · detail-driven', tone: 'sky',
    headerCls: 'bg-sky-50 border-sky-200', badgeCls: 'bg-sky-600',
    coach: {
      approach: 'Bring facts, structure and precision. Vagueness kills the sale — details build confidence.',
      do: ['Give exact prices, terms, inclusions', 'Compare options side-by-side', 'Follow up in writing'],
      dont: ['Hype and superlatives', 'Winging answers you\u2019re not sure of', 'Pushing before their questions are answered'],
      greenBrain: '“What information would help you feel confident this is the right choice?”',
      objection: 'Answer with data, offer a written summary, and agree a specific time to reconnect once they\u2019ve reviewed it.',
    },
  },
};

const BAR_COLORS = { D: 'bg-rose-500', I: 'bg-amber-400', S: 'bg-emerald-500', C: 'bg-sky-500' };

const Stat = ({ label, value, suffix }) => (
  <div className="text-center">
    <div className="text-xl font-bold text-slate-900 tabular-nums">{value ?? '—'}{value != null && suffix ? suffix : ''}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

const StyleCard = ({ row }) => {
  const meta = STYLE_META[row.style];
  return (
    <div className={`rounded-xl border ${meta.headerCls} overflow-hidden`} data-testid={`disc-card-${row.style}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className={`w-10 h-10 rounded-full ${meta.badgeCls} text-white font-black text-lg flex items-center justify-center shrink-0`}>
          {row.style}
        </span>
        <div className="min-w-0">
          <p className="font-bold text-slate-900 leading-tight">{meta.name}</p>
          <p className="text-[11px] text-slate-500">{meta.tag}</p>
        </div>
        <span className="ml-auto text-xs font-bold text-slate-600 bg-white/70 border border-slate-200 rounded-full px-2.5 py-1">
          {row.calls} calls · {row.share_pct}%
        </span>
      </div>
      <div className="bg-white px-4 py-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Avg score" value={row.avg_score} />
          <Stat label="Revival win rate" value={row.win_rate_pct} suffix="%" />
          <Stat label="Deals revivable" value={row.revivable_deals} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">How to coach &amp; sell to them</p>
          <p className="text-sm text-slate-700">{meta.coach.approach}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          <div className="bg-emerald-50 border border-emerald-100 rounded p-2">
            <p className="font-bold text-emerald-700 mb-1">Do</p>
            <ul className="space-y-0.5 text-slate-700 list-disc list-inside">
              {meta.coach.do.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </div>
          <div className="bg-rose-50 border border-rose-100 rounded p-2">
            <p className="font-bold text-rose-700 mb-1">Don&apos;t</p>
            <ul className="space-y-0.5 text-slate-700 list-disc list-inside">
              {meta.coach.dont.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs space-y-1.5">
          <p><span className="font-bold text-teal-700">Green Brain question:</span> <span className="italic text-slate-700">{meta.coach.greenBrain}</span></p>
          <p><span className="font-bold text-indigo-700">On objections:</span> <span className="text-slate-700">{meta.coach.objection}</span></p>
        </div>
        {row.top_moods?.length > 0 && (
          <p className="text-[11px] text-slate-500">
            <MessageCircle className="w-3 h-3 inline mr-1" />
            Common moods: {row.top_moods.map((m) => `${m.mood} (${m.count})`).join(' · ')}
          </p>
        )}
        {row.recent_calls?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Recent {row.style}-style calls</p>
            <div className="flex flex-wrap gap-1.5">
              {row.recent_calls.map((c) => (
                <Link
                  key={c.call_id}
                  to={`/calls/${c.call_id}`}
                  className="inline-flex items-center gap-1 text-[11px] text-teal-700 hover:text-teal-900 border border-teal-200 bg-teal-50 rounded-full px-2 py-0.5"
                  data-testid={`disc-recent-call-${c.call_id}`}
                >
                  <Phone className="w-3 h-3" />
                  {c.agent_name || 'Call'}{c.at ? ` · ${String(c.at).slice(0, 10)}` : ''}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function DiscInsightsPage() {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/disc-insights?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const styleRows = data?.styles || [];
  const total = data?.total_profiled || 0;

  return (
    <div className="max-w-6xl" data-testid="disc-insights-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Fingerprint className="w-6 h-6 text-teal-600" /> DISC Insights
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Your callers&apos; behavioural mix and exactly how to coach &amp; sell to each style — at a glance.
          </p>
        </div>
        <div className="flex gap-1.5">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs font-semibold rounded-lg px-3 py-1.5 border ${days === d ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`disc-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading DISC profiles…
        </div>
      )}

      {!loading && (!data?.enabled || total === 0) && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500" data-testid="disc-empty">
          <Fingerprint className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          No DISC profiles in the last {days} days yet — they build automatically as calls are graded.
        </div>
      )}

      {!loading && data?.enabled && total > 0 && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="disc-distribution">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-800">Caller mix — {total} profiled calls (last {data.days}d)</p>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Target className="w-3 h-3" /> Rule-based, no AI cost</p>
            </div>
            <div className="flex h-5 rounded-full overflow-hidden border border-slate-200">
              {styleRows.filter((r) => r.calls > 0).map((r) => (
                <div
                  key={r.style}
                  className={`${BAR_COLORS[r.style]} flex items-center justify-center text-[10px] font-bold text-white`}
                  style={{ width: `${Math.max(r.share_pct, 4)}%` }}
                  title={`${r.style}: ${r.calls} (${r.share_pct}%)`}
                >
                  {r.share_pct >= 8 ? `${r.style} ${r.share_pct}%` : r.style}
                </div>
              ))}
            </div>
            {(() => {
              const best = styleRows.filter((r) => r.win_rate_pct != null).sort((a, b) => b.win_rate_pct - a.win_rate_pct)[0];
              return best ? (
                <p className="text-[11px] text-emerald-700 mt-2 font-medium" data-testid="disc-best-style">
                  <Trophy className="w-3 h-3 inline mr-1" />
                  {STYLE_META[best.style].name} ({best.style}) callers convert best right now — {best.win_rate_pct}% revival win rate.
                </p>
              ) : null;
            })()}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {styleRows.map((r) => <StyleCard key={r.style} row={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
